/* eslint-disable react/jsx-no-bind -- Designed by MUI. */
/* eslint-disable no-console -- We want to log errors in case of failed calls. */
import { Autocomplete, Button, styled, TextField } from '@ori-ui/mui';
import type { CancelTokenSource } from '@ori/axios';
import { axios } from '@ori/axios';
import type { MutableRefObject, VFC, ChangeEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import {
  accessConsultantUrl,
  basketUrl,
  getConsultantNumbersUrl,
  getCustomAxios,
  vipAccessSwitchUrl,
  vipAccessSwitchValidateUrl,
} from '~/helpers';
import type {
  AccessConsultantResponse,
  ConsultantNumberHint,
  GetConsultantNumbersResponse,
  VipAccessLabels,
  VipAccessValidateResponse,
} from '~/models';
import { TranslationKeys } from '~/models';

const VipOrderFormRoot = styled('form')(({ theme }) => ({
  alignItems: 'center',
  columnGap: theme.spacing(4),
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  position: 'relative',
}));

const VipOrderFormInput = styled(TextField)(() => ({
  width: 200,
}));

const VipOrderFormSubmit = styled(Button)(() => ({
  width: 175,
}));

interface VipOrderFormProps {
  vipAccessLabels: VipAccessLabels;
}

export const VipOrderForm: VFC<VipOrderFormProps> = ({
  vipAccessLabels: { ConsultantChangeConsultantNumber },
}) => {
  const cancelToken: MutableRefObject<CancelTokenSource | null> = useRef(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [areHintsLoading, setAreHintsLoading] = useState(false);
  const [consultantNumber, setConsultantNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hints, setHints] = useState<ConsultantNumberHint[]>([]);
  const [isSubmitLocked, setIsSubmitLocked] = useState(true);
  const [translate] = useTranslation();

  const { currentLanguage } = useAppConfigurationContext();

  const { register, handleSubmit } = useForm();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setConsultantNumber(value);
      setErrorMessage(null);
      setAreHintsLoading(true);
      setIsSubmitLocked(true);

      if (!value) {
        return;
      }

      if (cancelToken.current?.cancel) {
        cancelToken.current.cancel('Operation canceled due to new request.');
      }

      cancelToken.current = { ...axios.CancelToken.source() };

      getCustomAxios(currentLanguage)
        .get<GetConsultantNumbersResponse>(getConsultantNumbersUrl(value), {
          cancelToken: cancelToken.current.token,
        })
        .then(({ data }) => setHints(data.map((hint, index) => ({ index, hint }))))
        .catch((error: unknown) => console.error(error))
        .finally(() => {
          setAreHintsLoading(false);
        });
    },
    [currentLanguage],
  );

  const handleSelect = useCallback(
    (event: ChangeEvent<unknown>) => {
      const id = (event.target as HTMLInputElement).textContent;
      setConsultantNumber(id ?? '');
      setHints([]);

      if (!id) {
        return;
      }

      getCustomAxios(currentLanguage)
        .post<VipAccessValidateResponse>(vipAccessSwitchValidateUrl(id))
        .then(({ data: { message, result } }) => {
          setErrorMessage(message);
          setIsSubmitLocked(!result);
        })
        .catch((error: unknown) => console.error(error));
    },
    [currentLanguage],
  );

  const onSubmit = useCallback(async () => {
    try {
      const responseConsultant = await getCustomAxios(
        currentLanguage,
      ).get<AccessConsultantResponse>(accessConsultantUrl(consultantNumber));

      const {
        ConsultantNumber: consultant,
        ErrorMessage,
        IsAccessGranted,
      } = responseConsultant.data;

      if (IsAccessGranted) {
        try {
          await getCustomAxios(currentLanguage).post(vipAccessSwitchUrl(consultant));

          if (!formRef.current) {
            return;
          }

          formRef.current.submit();
        } catch (error: unknown) {
          setErrorMessage(translate(TranslationKeys.FailedToSubmit));
          console.warn(error);
        }
      } else {
        setErrorMessage(ErrorMessage);
      }
    } catch (error: unknown) {
      console.warn(error);
    }
  }, [consultantNumber, translate, currentLanguage]);

  return (
    <VipOrderFormRoot
      ref={formRef}
      action={basketUrl}
      autoComplete="off"
      method="POST"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Autocomplete
        getOptionLabel={(hint) => String(hint.hint)}
        id="ConsultantNumber"
        loading={areHintsLoading}
        loadingText={translate(TranslationKeys.Loading)}
        noOptionsText={translate(TranslationKeys.NoOptions)}
        options={hints}
        renderInput={(props) => (
          <VipOrderFormInput
            variant="standard"
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            label={ConsultantChangeConsultantNumber}
            {...register(props.id)}
            onChange={handleChange}
            {...props}
          />
        )}
        onChange={handleSelect}
      />
      <VipOrderFormSubmit
        disabled={isSubmitLocked}
        color="primary"
        type="submit"
        variant="contained"
      >
        {translate(TranslationKeys.Change)}
      </VipOrderFormSubmit>
    </VipOrderFormRoot>
  );
};
